// FAZ-10.6 / K-31 + K-40 — blok başı yapılandırılmış stil modeli. BE
// `ComponentStyleJson.cs` aynası: styleJson artık serbest-metin DEĞİL, bilinen
// anahtar seti (color/backgroundColor/fontSize/fontWeight/width/height/padding/
// marginTop/marginBottom/borderRadius) + K-40 responsive override (tablet/mobile,
// AYNI taban anahtarlar, iç-içe nesting YOK) + görünürlük (`hiddenOn`).
//
// Bu modül stil panelinin (K-32) VE render'ın (blockViews/ComponentJsonRenderer)
// ortak veri katmanıdır: panel `parseStyleJson`→model→düzenleme→`serializeStyleJson`
// yaparken, render `resolveBlockStyle` ile taban inline CSS + scoped-class media
// query `<style>` üretir. Değerler burada BE ile birebir aynı kurallarla süzülür
// (bozuk/aralık-dışı değer sessizce düşer) → render güvenliği yapısal.

import type { CSSProperties } from 'react';

/** BE `ComponentStyleJson.FontWeights` aynası. */
export const STYLE_FONT_WEIGHTS = ['normal', 'medium', 'bold'] as const;
export type StyleFontWeight = (typeof STYLE_FONT_WEIGHTS)[number];

/** BE `ComponentStyleJson.Breakpoints` aynası (mobile ≤640, tablet 641-1024, desktop ≥1025). */
export const STYLE_BREAKPOINTS = ['mobile', 'tablet', 'desktop'] as const;
export type StyleBreakpoint = (typeof STYLE_BREAKPOINTS)[number];

/** BE `ComponentConstraints` stil aralıkları aynası. */
export const STYLE_RANGES = {
  fontSize: { min: 12, max: 72 },
  padding: { min: 0, max: 96 },
  margin: { min: 0, max: 96 },
  borderRadius: { min: 0, max: 48 },
  widthPercent: { min: 1, max: 100 },
  widthPx: { min: 1, max: 1200 },
  heightPx: { min: 1, max: 1200 },
} as const;

/** BE `ComponentStyleJson.CanonicalKeyOrder` aynası — taban bildirimlerinin sırası. */
export const STYLE_BASE_KEY_ORDER = [
  'color',
  'backgroundColor',
  'fontSize',
  'fontWeight',
  'width',
  'height',
  'padding',
  'marginTop',
  'marginBottom',
  'borderRadius',
] as const;
export type StyleBaseKey = (typeof STYLE_BASE_KEY_ORDER)[number];

/** Taban stil bildirimleri — hepsi opsiyonel. */
export interface StyleBase {
  color?: string;
  backgroundColor?: string;
  fontSize?: number;
  fontWeight?: StyleFontWeight;
  width?: string;
  height?: string;
  padding?: number;
  marginTop?: number;
  marginBottom?: number;
  borderRadius?: number;
}

/** Panel/render'ın tükettiği tam stil modeli (taban + responsive + görünürlük). */
export interface StyleModel {
  base: StyleBase;
  hiddenOn: StyleBreakpoint[];
  tablet: StyleBase;
  mobile: StyleBase;
}

export const EMPTY_STYLE_MODEL: StyleModel = { base: {}, hiddenOn: [], tablet: {}, mobile: {} };

// BE `ComponentStyleJson` regex'leri aynası.
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const THEME_VAR = /^var\(--ds-[a-z0-9-]+(?:,\s*#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}))?\)$/;
const WIDTH_UNIT = /^([0-9]{1,4})(%|px)$/;

function isColor(value: unknown): value is string {
  return typeof value === 'string' && (HEX_COLOR.test(value) || THEME_VAR.test(value));
}

function intInRange(value: unknown, min: number, max: number): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value)) return undefined;
  return value >= min && value <= max ? value : undefined;
}

function fontWeight(value: unknown): StyleFontWeight | undefined {
  return typeof value === 'string' && (STYLE_FONT_WEIGHTS as readonly string[]).includes(value)
    ? (value as StyleFontWeight)
    : undefined;
}

function width(value: unknown): string | undefined {
  if (value === 'auto') return 'auto';
  if (typeof value !== 'string') return undefined;
  const m = WIDTH_UNIT.exec(value);
  if (!m) return undefined;
  const num = Number(m[1]);
  const range = m[2] === '%' ? STYLE_RANGES.widthPercent : STYLE_RANGES.widthPx;
  return num >= range.min && num <= range.max ? value : undefined;
}

function height(value: unknown): string | undefined {
  if (value === 'auto') return 'auto';
  if (typeof value !== 'string') return undefined;
  const m = WIDTH_UNIT.exec(value);
  if (!m || m[2] === '%') return undefined;
  const num = Number(m[1]);
  return num >= STYLE_RANGES.heightPx.min && num <= STYLE_RANGES.heightPx.max ? value : undefined;
}

/** Ham nesneyi (JSON parse çıktısı) süzülmüş `StyleBase`'e çevirir — geçersiz düşer. */
function toBase(raw: Record<string, unknown>): StyleBase {
  const base: StyleBase = {};
  if (isColor(raw.color)) base.color = raw.color;
  if (isColor(raw.backgroundColor)) base.backgroundColor = raw.backgroundColor;
  const fs = intInRange(raw.fontSize, STYLE_RANGES.fontSize.min, STYLE_RANGES.fontSize.max);
  if (fs !== undefined) base.fontSize = fs;
  const fw = fontWeight(raw.fontWeight);
  if (fw) base.fontWeight = fw;
  const w = width(raw.width);
  if (w) base.width = w;
  const h = height(raw.height);
  if (h) base.height = h;
  const pad = intInRange(raw.padding, STYLE_RANGES.padding.min, STYLE_RANGES.padding.max);
  if (pad !== undefined) base.padding = pad;
  const mt = intInRange(raw.marginTop, STYLE_RANGES.margin.min, STYLE_RANGES.margin.max);
  if (mt !== undefined) base.marginTop = mt;
  const mb = intInRange(raw.marginBottom, STYLE_RANGES.margin.min, STYLE_RANGES.margin.max);
  if (mb !== undefined) base.marginBottom = mb;
  const br = intInRange(raw.borderRadius, STYLE_RANGES.borderRadius.min, STYLE_RANGES.borderRadius.max);
  if (br !== undefined) base.borderRadius = br;
  return base;
}

function hiddenOn(raw: unknown): StyleBreakpoint[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: StyleBreakpoint[] = [];
  for (const bp of STYLE_BREAKPOINTS) {
    if (raw.includes(bp) && !seen.has(bp)) {
      seen.add(bp);
      out.push(bp);
    }
  }
  return out;
}

/**
 * Ham styleJson metnini süzülmüş `StyleModel`'e çevirir. Geçersiz JSON / bilinmeyen
 * anahtar / aralık-dışı değer sessizce düşer (BE zaten reddeder; bu render güvenliği).
 */
export function parseStyleJson(json: string | null | undefined): StyleModel {
  if (!json || !json.trim()) return { base: {}, hiddenOn: [], tablet: {}, mobile: {} };
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { base: {}, hiddenOn: [], tablet: {}, mobile: {} };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { base: {}, hiddenOn: [], tablet: {}, mobile: {} };
  }
  const obj = raw as Record<string, unknown>;
  const tabletRaw = obj.tablet;
  const mobileRaw = obj.mobile;
  return {
    base: toBase(obj),
    hiddenOn: hiddenOn(obj.hiddenOn),
    tablet: tabletRaw && typeof tabletRaw === 'object' && !Array.isArray(tabletRaw)
      ? toBase(tabletRaw as Record<string, unknown>)
      : {},
    mobile: mobileRaw && typeof mobileRaw === 'object' && !Array.isArray(mobileRaw)
      ? toBase(mobileRaw as Record<string, unknown>)
      : {},
  };
}

function baseHasKeys(base: StyleBase): boolean {
  return STYLE_BASE_KEY_ORDER.some((k) => base[k] !== undefined);
}

/** `StyleBase`'i BE canonical anahtar-sırasında düz nesneye yazar (present-only). */
function baseToOrderedObject(base: StyleBase): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of STYLE_BASE_KEY_ORDER) {
    const value = base[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * `StyleModel`'i BE-sözleşme styleJson metnine çevirir. Tümüyle boş model → '' (alan
 * hiç gönderilmez). Anahtar sırası BE canonical ile birebir → save round-trip'te
 * sahte "kirli" durumu oluşmaz.
 */
export function serializeStyleJson(model: StyleModel): string {
  const out: Record<string, unknown> = baseToOrderedObject(model.base);
  if (model.hiddenOn.length > 0) {
    out.hiddenOn = STYLE_BREAKPOINTS.filter((bp) => model.hiddenOn.includes(bp));
  }
  if (baseHasKeys(model.tablet)) out.tablet = baseToOrderedObject(model.tablet);
  if (baseHasKeys(model.mobile)) out.mobile = baseToOrderedObject(model.mobile);
  return Object.keys(out).length === 0 ? '' : JSON.stringify(out);
}

/** `fontWeight` token'ını geçerli CSS değerine map'ler ('medium' geçersiz → 500). */
function cssFontWeight(fw: StyleFontWeight): number | string {
  return fw === 'medium' ? 500 : fw;
}

/** `StyleBase`'i React inline `CSSProperties`'e çevirir (sayılara React px ekler). */
export function styleBaseToCss(base: StyleBase): CSSProperties {
  const css: CSSProperties = {};
  if (base.color) css.color = base.color;
  if (base.backgroundColor) css.backgroundColor = base.backgroundColor;
  if (base.fontSize !== undefined) css.fontSize = base.fontSize;
  if (base.fontWeight) css.fontWeight = cssFontWeight(base.fontWeight);
  if (base.width) css.width = base.width;
  if (base.height) css.height = base.height;
  if (base.padding !== undefined) css.padding = base.padding;
  if (base.marginTop !== undefined) css.marginTop = base.marginTop;
  if (base.marginBottom !== undefined) css.marginBottom = base.marginBottom;
  if (base.borderRadius !== undefined) css.borderRadius = base.borderRadius;
  return css;
}

// px eklenecek uzunluk anahtarları (media-query metin üretiminde).
const PX_KEYS = new Set<StyleBaseKey>([
  'fontSize',
  'padding',
  'marginTop',
  'marginBottom',
  'borderRadius',
]);
const KEBAB: Record<StyleBaseKey, string> = {
  color: 'color',
  backgroundColor: 'background-color',
  fontSize: 'font-size',
  fontWeight: 'font-weight',
  width: 'width',
  height: 'height',
  padding: 'padding',
  marginTop: 'margin-top',
  marginBottom: 'margin-bottom',
  borderRadius: 'border-radius',
};

/** `StyleBase`'i media-query gövdesi için `!important`'lı CSS metnine çevirir. */
function baseToImportantCss(base: StyleBase): string {
  const parts: string[] = [];
  for (const key of STYLE_BASE_KEY_ORDER) {
    const value = base[key];
    if (value === undefined) continue;
    const cssValue =
      key === 'fontWeight'
        ? String(cssFontWeight(value as StyleFontWeight))
        : PX_KEYS.has(key)
          ? `${value}px`
          : String(value);
    parts.push(`${KEBAB[key]}:${cssValue} !important`);
  }
  return parts.join(';');
}

// BE breakpoint aynası: mobile ≤640, tablet 641-1024, desktop ≥1025.
const MEDIA: Record<StyleBreakpoint, string> = {
  mobile: '(max-width:640px)',
  tablet: '(min-width:641px) and (max-width:1024px)',
  desktop: '(min-width:1025px)',
};

/** Küçük deterministik hash (FNV-1a) — aynı styleJson aynı scoped class'ı üretir. */
function hashClass(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `cs-s-${(h >>> 0).toString(36)}`;
}

export interface ResolvedBlockStyle {
  /** Taban (desktop) bildirimleri — inline; token'lardan SONRA merge edilir (stil kazanır). */
  style: CSSProperties;
  /** Responsive/görünürlük varsa blok'a eklenen benzersiz sınıf. */
  scopedClass?: string;
  /** Responsive/görünürlük varsa render edilecek `<style>` gövdesi. */
  css?: string;
}

/**
 * Bir bloğun styleJson'ını render için çözer: taban → inline CSS; tablet/mobile
 * override + hiddenOn → scoped-class media query CSS (`!important` ile inline
 * taban'ı ilgili breakpoint'te ezer). Böylece desktop'ta stil token'ları ezer
 * (K-32), tablet/mobile'da override kazanır (K-40).
 */
export function resolveBlockStyle(json: string | null | undefined): ResolvedBlockStyle {
  const model = parseStyleJson(json);
  const style = styleBaseToCss(model.base);

  const rules: string[] = [];
  const tabletCss = baseToImportantCss(model.tablet);
  const mobileCss = baseToImportantCss(model.mobile);

  // Scoped class'ı yalnız responsive/görünürlük varsa üret (basit blok = sıfır ek CSS).
  const scopedClass =
    tabletCss || mobileCss || model.hiddenOn.length > 0 ? hashClass(json ?? '') : undefined;

  if (scopedClass) {
    if (tabletCss) rules.push(`@media ${MEDIA.tablet}{.${scopedClass}{${tabletCss}}}`);
    if (mobileCss) rules.push(`@media ${MEDIA.mobile}{.${scopedClass}{${mobileCss}}}`);
    for (const bp of model.hiddenOn) {
      rules.push(`@media ${MEDIA[bp]}{.${scopedClass}{display:none !important}}`);
    }
  }

  return { style, scopedClass, css: rules.length > 0 ? rules.join('') : undefined };
}
